import React from 'react';
import { ArrowRight, CreditCard, FileText, ShieldCheck } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import ConsultantPricingSchedule from '../Agreements/ConsultantPricingSchedule';

const titleCase = (value = '') =>
  String(value)
    .replaceAll('-', ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const money = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

/**
 * Consultant commercial programme and accepted client pricing schedule.
 */
const ConsultantBillingOverview = ({ programme, catalog, onViewAgreements }) => {
  if (!programme || !catalog) {
    return <p className='text-sm text-muted-foreground'>Loading your consultant billing programme…</p>;
  }

  const nextLevel =
    programme.level === 'foundation'
      ? catalog.levels?.growth
      : programme.level === 'growth'
        ? catalog.levels?.scale
        : null;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h2 className='flex items-center gap-2 text-lg font-semibold text-foreground'>
            <CreditCard size={20} strokeWidth={1.5} />
            Plans & billing
          </h2>
          <p className='mt-1 text-sm text-muted-foreground'>
            Your programme terms and the accepted pricing available for client allocation.
          </p>
        </div>
        <Button variant='outline' onClick={onViewAgreements}>
          <FileText size={16} strokeWidth={1.5} />
          Agreement history & PDFs
          <ArrowRight size={16} strokeWidth={1.5} />
        </Button>
      </div>

      <div className='grid gap-3 md:grid-cols-4'>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Programme</p>
            <div className='mt-1 flex items-center gap-2'>
              <p className='text-xl font-semibold text-foreground'>{titleCase(programme.level)}</p>
              <Badge>{programme.discountPercent}% discount</Badge>
            </div>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Active portfolio</p>
            <p className='mt-1 text-xl font-semibold text-foreground'>
              {programme.clientCount} clients · {programme.points} points
            </p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Commitment</p>
            <p className='mt-1 text-sm font-medium text-foreground'>
              {programme.commitmentEndDate || 'Not recorded'}
            </p>
            <p className='text-xs text-muted-foreground'>{programme.noticePeriodDays} days’ notice</p>
          </CardContent>
        </Card>
        <Card className='bg-card'>
          <CardContent className='p-4'>
            <p className='text-xs text-muted-foreground'>Settlement</p>
            <p className='mt-1 text-sm font-medium text-foreground'>{titleCase(programme.chargeCadence)}</p>
            <p className='text-xs text-muted-foreground'>
              {programme.annualPortfolioMinimum
                ? `${money(programme.annualPortfolioMinimum)} annual minimum`
                : 'No annual minimum recorded'}
            </p>
          </CardContent>
        </Card>
      </div>

      {nextLevel && (
        <Card className='border-info bg-info-bg'>
          <CardContent className='flex items-center gap-3 p-4 text-info'>
            <ShieldCheck size={20} strokeWidth={1.5} />
            <p className='text-sm'>
              Next: {titleCase(nextLevel.id)} at {nextLevel.minClients} clients and {nextLevel.minPoints} points
              ({nextLevel.discountPercent}% discount).
            </p>
          </CardContent>
        </Card>
      )}

      {catalog.pricingSchedule ? (
        <ConsultantPricingSchedule schedule={catalog.pricingSchedule} />
      ) : (
      <Card className='bg-card'>
        <CardHeader>
          <CardTitle className='text-base'>Accepted Plan × FTE schedule · version {programme.pricingScheduleVersion}</CardTitle>
        </CardHeader>
        <CardContent className='p-0'>
          <Table>
            <TableHeader>
              <TableRow className='border-border'>
                <TableHead>Plan</TableHead>
                {catalog.fteBands.map((band) => <TableHead key={band}>{band} FTE</TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {catalog.plans.map((plan) => (
                <TableRow key={plan} className='border-border'>
                  <TableCell className='font-medium text-foreground'>{titleCase(plan)}</TableCell>
                  {catalog.fteBands.map((band) => (
                    <TableCell key={band}>
                      {(() => {
                        const list = Number(catalog.licensePrices?.[plan]?.[band]) || 0;
                        const net = Math.round(list * (1 - Number(programme.discountPercent || 0) / 100) * 100) / 100;
                        return (
                          <div className='space-y-0.5'>
                            <p className='text-xs text-muted-foreground line-through'>{money(list)} list</p>
                            <p className='font-medium text-foreground'>{money(net)} net</p>
                            <p className='text-xs text-success'>Save {money(list - net)}</p>
                          </div>
                        );
                      })()}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <p className='p-4 text-xs text-muted-foreground'>
            Annual GBP prices after your {programme.discountPercent}% {titleCase(programme.level)} programme discount.
            A 36-month client commitment receives a further 5% when allocated.
          </p>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default ConsultantBillingOverview;
